import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { LogbookQuickRange } from './LogbookQuickRange';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import {
  Loader2, Search, X, Bluetooth, CreditCard, Truck, Container, Gauge, Upload, Mail,
  RefreshCw, Calendar as CalendarIcon, MapPin, Clock, Route,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDT, formatDuration } from '../../utils/timezone';
import { OCCUPANCY_BADGE_CLS } from '../../utils/occupancy';
import { logReportExport } from '../../utils/auditExport';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { EmailReportModal } from '../EmailReportModal';
import { PaginationFooter, usePersistedPageSize } from '../shared/PaginationFooter';
import { fmtCoords, tripLocMapsUrl } from '../../utils/tripExport';
import {
  exportAssetEntryCsv, exportAssetEntryPdf, exportAllAssetsCsv, exportAllAssetsPdf, buildAllAssetsPdf,
} from '../../utils/assetLogExport';

const TYPE_META = {
  trailer: { label: 'Trailer', icon: Truck, cls: OCCUPANCY_BADGE_CLS.trailer },
  equipment: { label: 'Equipment', icon: Container, cls: OCCUPANCY_BADGE_CLS.equipment },
};

const ENTRIES_PER_PAGE_DEFAULT = 10;

const buildQuery = (filters) => {
  const p = new URLSearchParams();
  if (filters.start_time) p.set('start_time', new Date(filters.start_time).toISOString());
  if (filters.end_time) p.set('end_time', new Date(filters.end_time).toISOString());
  if (filters.entity_type && filters.entity_type !== 'all') p.set('entity_type', filters.entity_type);
  if (filters.source && filters.source !== 'all') p.set('source', filters.source);
  if ((filters.search || '').trim()) p.set('search', filters.search.trim());
  return p;
};

// Vehicle Asset Log Book — trailer & equipment attach/detach history. Visually and functionally
// mirrors the Vehicle Driver Log Book (calendar filters, search, export CSV/PDF/Email, pagination,
// dark/light parity). Assets are physically coupled, so each row is one attachment session.
export const AssetLogbookDialog = ({ open, onOpenChange, device, api }) => {
  const { isNotificationChannelActive } = useFeatureFlags();
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const [filters, setFilters] = useState({ start_time: null, end_time: null, entity_type: 'all', source: 'all', search: '' });
  const [selected, setSelected] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const suppressPageFetchRef = useRef(false);
  const [entriesPerPage, setEntriesPerPage] = usePersistedPageSize('vehicle-asset-logbook', 10);
  const entriesListRef = useRef(null);
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [emailModal, setEmailModal] = useState({ open: false, ctx: null });

  const vehicleName = device?.asset_name || device?.registration || device?.name || 'this vehicle';

  // True server-side pagination: fetch one page (`skip`/`limit`), total from X-Total-Count.
  const fetchPage = useCallback(async (f, pageArg, size) => {
    const params = buildQuery(f);
    params.set('limit', String(size));
    params.set('skip', String((pageArg - 1) * size));
    const res = await api.get(`/fleet/vehicles/${device.id}/asset-logbook?${params.toString()}`);
    setTotalCount(parseInt(res.headers?.['x-total-count'], 10) || res.data?.count || 0);
    return res;
  }, [api, device]);

  // Full matching window — used by Select All + exports only.
  const fetchAllEntries = useCallback(async () => {
    const params = buildQuery(filters);
    params.set('limit', '1000');
    params.set('skip', '0');
    const res = await api.get(`/fleet/vehicles/${device.id}/asset-logbook?${params.toString()}`);
    return res.data?.entries || [];
  }, [api, device, filters]);

  const load = useCallback(async (f) => {
    if (!device) return;
    setState({ loading: true, data: null, error: null });
    setSelected(new Set());
    setPage((prev) => { if (prev !== 1) suppressPageFetchRef.current = true; return 1; });
    try {
      const res = await fetchPage(f, 1, entriesPerPage);
      setState({ loading: false, data: res.data, error: null });
    } catch (e) {
      const message = e.response?.data?.detail || 'Unable to load asset log book';
      setState({ loading: false, data: null, error: message });
      toast.error(message);
    }
  }, [device, fetchPage, entriesPerPage]);

  // Page / rows-per-page changes swap the data in place (no spinner remount).
  useEffect(() => {
    if (suppressPageFetchRef.current) { suppressPageFetchRef.current = false; return; }
    if (!open || !device || !state.data) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchPage(filters, page, entriesPerPage);
        if (!cancelled) setState((prev) => ({ ...prev, loading: false, data: res.data, error: null }));
      } catch { /* keep showing the existing page */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, entriesPerPage]);

  useEffect(() => {
    if (open && device) {
      const init = { start_time: null, end_time: null, entity_type: 'all', source: 'all', search: '' };
      setFilters(init);
      load(init);
    }
  }, [open, device, load]);

  const entries = state.data?.entries || [];
  const utilisation = state.data?.utilisation || [];

  const toggleSelection = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const logExport = (fmt, count) => logReportExport(api, {
    report: 'Vehicle Asset Log Book',
    format: fmt,
    scope: vehicleName,
    count,
    entity_id: device?.id,
  });

  const exportAll = async (fmt) => {
    try {
      const all = await fetchAllEntries();
      const list = selected.size ? all.filter((e) => selected.has(e.id)) : all;
      if (!list.length) { toast.info('No attachments to export'); return; }
      logExport(fmt, list.length);
      if (fmt === 'pdf') {
        exportAllAssetsPdf(list, vehicleName, selected.size ? [] : utilisation).catch(() => toast.error('Could not generate PDF'));
      } else {
        exportAllAssetsCsv(list, vehicleName, selected.size ? [] : utilisation);
      }
    } catch { toast.error('Could not load attachments for export'); }
  };

  const openEmail = () => {
    const count = selected.size || totalCount;
    if (!count) { toast.info('No attachments to email'); return; }
    setEmailModal({
      open: true,
      ctx: {
        reportLabel: 'Vehicle Asset Log Book',
        scope: vehicleName,
        count,
        subject: `EnsueLIVETRAK - Vehicle Asset Log Book - ${vehicleName}`,
        buildPdf: async () => {
          const all = await fetchAllEntries();
          const list = selected.size ? all.filter((e) => selected.has(e.id)) : all;
          return buildAllAssetsPdf(list, vehicleName, selected.size ? [] : utilisation);
        },
      },
    });
  };

  const total = totalCount;
  const pageStart = (page - 1) * entriesPerPage;
  const pageEntries = entries;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-asset-logbook-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5 text-[#2563eb]" /> Vehicle Asset Log Book</DialogTitle>
            <DialogDescription>
              Trailer &amp; equipment attach/detach history for {vehicleName}. Assets stay coupled while parked, so each row is one attachment session.
            </DialogDescription>
          </DialogHeader>

          {state.loading ? (
            <div className="py-8 flex justify-center" data-testid="vehicle-asset-logbook-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : state.error ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200/90" data-testid="vehicle-asset-logbook-error">{state.error}</div>
          ) : (
            <div className="space-y-4" data-testid="vehicle-asset-logbook-content">
              {state.data?.identity_logbook && !state.data.identity_logbook.enabled && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800/70 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200" data-testid="vehicle-asset-logbook-disabled-notice">
                  Identity Logbooks are disabled for new trips on this vehicle — showing historical data only.
                </div>
              )}
              {/* ── Filters (mirrors Vehicle Driver Log Book) ── */}
              <div className="rounded-lg border p-4 bg-background" data-testid="vehicle-asset-logbook-filters">
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label>From</Label>
                    <div className="relative">
                      <Popover open={startOpen} onOpenChange={setStartOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full h-9 text-xs font-normal justify-between pr-8" data-testid="vehicle-asset-logbook-start-filter">
                            <span className="truncate text-left min-w-0 flex-1">{filters.start_time ? formatDT(filters.start_time, { seconds: false }) : 'Start date'}</span>
                            {!filters.start_time && <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50 shrink-0" />}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                          <Calendar mode="single" selected={filters.start_time || undefined} onSelect={(date) => { if (!date) return; const prev = filters.start_time; date.setHours(prev?.getHours() || 0, prev?.getMinutes() || 0, 0, 0); setFilters((p) => ({ ...p, start_time: date })); }} />
                          <div className="flex items-center gap-2 p-2 border-t">
                            <Input type="time" value={filters.start_time ? `${String(filters.start_time.getHours()).padStart(2, '0')}:${String(filters.start_time.getMinutes()).padStart(2, '0')}` : '00:00'} onChange={(e) => { const [h, m] = e.target.value.split(':').map(Number); setFilters((p) => { const d = new Date(p.start_time || new Date()); d.setHours(h || 0, m || 0, 0, 0); return { ...p, start_time: d }; }); }} className="h-8 text-sm flex-1" data-testid="vehicle-asset-logbook-start-time" />
                            {filters.start_time && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setFilters((p) => ({ ...p, start_time: null }))}>Clear</Button>}
                            <Button size="sm" className="h-8 text-xs" onClick={() => setStartOpen(false)}>Done</Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                      {filters.start_time && (
                        <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); setFilters((p) => ({ ...p, start_time: null })); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="vehicle-asset-logbook-clear-start"><X className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label>To</Label>
                    <div className="relative">
                      <Popover open={endOpen} onOpenChange={setEndOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full h-9 text-xs font-normal justify-between pr-8" data-testid="vehicle-asset-logbook-end-filter">
                            <span className="truncate text-left min-w-0 flex-1">{filters.end_time ? formatDT(filters.end_time, { seconds: false }) : 'End date'}</span>
                            {!filters.end_time && <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50 shrink-0" />}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                          <Calendar mode="single" selected={filters.end_time || undefined} onSelect={(date) => { if (!date) return; const prev = filters.end_time; date.setHours(prev?.getHours() || 23, prev?.getMinutes() || 59, 59, 999); setFilters((p) => ({ ...p, end_time: date })); }} />
                          <div className="flex items-center gap-2 p-2 border-t">
                            <Input type="time" value={filters.end_time ? `${String(filters.end_time.getHours()).padStart(2, '0')}:${String(filters.end_time.getMinutes()).padStart(2, '0')}` : '23:59'} onChange={(e) => { const [h, m] = e.target.value.split(':').map(Number); setFilters((p) => { const d = new Date(p.end_time || new Date()); d.setHours(h || 0, m || 0, 59, 999); return { ...p, end_time: d }; }); }} className="h-8 text-sm flex-1" data-testid="vehicle-asset-logbook-end-time" />
                            {filters.end_time && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setFilters((p) => ({ ...p, end_time: null }))}>Clear</Button>}
                            <Button size="sm" className="h-8 text-xs" onClick={() => setEndOpen(false)}>Done</Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                      {filters.end_time && (
                        <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); setFilters((p) => ({ ...p, end_time: null })); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="vehicle-asset-logbook-clear-end"><X className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </div>
                  <LogbookQuickRange
                    className="md:col-span-2"
                    startTime={filters.start_time}
                    endTime={filters.end_time}
                    onSelect={(start) => setFilters((p) => ({ ...p, start_time: start, end_time: null }))}
                    testIdPrefix="vehicle-asset-logbook"
                  />
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label>Type</Label>
                    <Select value={filters.entity_type} onValueChange={(v) => setFilters((p) => ({ ...p, entity_type: v }))}>
                      <SelectTrigger data-testid="vehicle-asset-logbook-type-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="trailer">Trailer</SelectItem>
                        <SelectItem value="equipment">Equipment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Source</Label>
                    <Select value={filters.source} onValueChange={(v) => setFilters((p) => ({ ...p, source: v }))}>
                      <SelectTrigger data-testid="vehicle-asset-logbook-source-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="rfid_ibutton">RFID / iButton</SelectItem>
                        <SelectItem value="ble">BLE Beacon</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto_auto]">
                  <div className="relative">
                    <Input value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); load(filters); } }} placeholder="Search asset, beacon id" className="pr-9" data-testid="vehicle-asset-logbook-search-filter" />
                    {filters.search && <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setFilters((p) => ({ ...p, search: '' }))} data-testid="vehicle-asset-logbook-clear-search-button"><X className="w-4 h-4" /></Button>}
                  </div>
                  <Button onClick={() => load(filters)} data-testid="vehicle-asset-logbook-apply-button"><Search className="w-4 h-4 mr-1" /> Search</Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" data-testid="vehicle-asset-logbook-export-button"><Upload className="w-4 h-4 mr-1" /> Export{selected.size ? ` (${selected.size})` : ''}</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => exportAll('csv')} data-testid="vehicle-asset-logbook-export-csv">CSV</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportAll('pdf')} data-testid="vehicle-asset-logbook-export-pdf">PDF</DropdownMenuItem>
                      <DropdownMenuItem onClick={openEmail} disabled={!isNotificationChannelActive('notification_email')} data-testid="vehicle-asset-logbook-export-email"><Mail className="w-3.5 h-3.5 mr-2" /> Email PDF{!isNotificationChannelActive('notification_email') && <span className="ml-auto text-[10px] text-destructive">Restricted</span>}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* ── Summary ── */}
              <div className="rounded-lg border p-4 bg-muted/20 grid gap-3 md:grid-cols-3 text-sm">
                <div><p className="text-xs text-muted-foreground">Vehicle</p><p className="font-semibold">{vehicleName}</p></div>
                <div><p className="text-xs text-muted-foreground">Attachments</p><p className="font-semibold" data-testid="vehicle-asset-logbook-count">{total}</p></div>
                <div><p className="text-xs text-muted-foreground">Sources</p><p data-testid="vehicle-asset-logbook-sources">{(state.data?.sources || []).join(', ') || '—'}</p></div>
              </div>

              {/* ── Utilisation ── */}
              {utilisation.length > 0 && (
                <div className="rounded-lg border p-3" data-testid="vehicle-asset-logbook-utilisation">
                  <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" /> Utilisation — attached time &amp; trips per asset</p>
                  <div className="space-y-1.5">
                    {utilisation.map((u, i) => {
                      const tm2 = TYPE_META[String(u.asset_type || '').toLowerCase()] || { label: u.asset_type || 'Asset', icon: Container };
                      const UIcon = tm2.icon;
                      return (
                        <div key={u.asset_key || i} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`vehicle-asset-logbook-util-${i}`}>
                          <span className="flex items-center gap-1.5 min-w-0">
                            <UIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            <span className="font-medium truncate" data-testid={`vehicle-asset-logbook-util-name-${i}`}>{u.asset_name || 'Unknown asset'}</span>
                            {u.active && <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-[10px]">Attached now</Badge>}
                          </span>
                          <span className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                            <span data-testid={`vehicle-asset-logbook-util-trips-${i}`}>{u.trips} {u.trips === 1 ? 'trip' : 'trips'}</span>
                            <span className="font-medium text-foreground" data-testid={`vehicle-asset-logbook-util-time-${i}`}>{formatDuration(u.total_attached_seconds)} attached</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {(state.data?.hints || []).map((h, i) => (
                <div key={i} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200/90">{h}</div>
              ))}

              {/* ── Select-all + Refresh ── */}
              {total > 0 && (() => {
                const pageAllOn = pageEntries.length > 0 && pageEntries.every((e) => selected.has(e.id));
                return (
                <div ref={entriesListRef} className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground pl-[15px]">
                    <Checkbox
                      checked={pageAllOn}
                      onCheckedChange={(checked) => setSelected((prev) => {
                        const next = new Set(prev);
                        pageEntries.forEach((e) => { if (checked) next.add(e.id); else next.delete(e.id); });
                        return next;
                      })}
                      data-testid="vehicle-asset-logbook-select-all"
                    />
                    <span data-testid="vehicle-asset-logbook-selection-count">{selected.size} selected</span>
                    {selected.size > 0 && (<>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={async () => {
                        if (selected.size >= totalCount) { setSelected(new Set()); return; }
                        try { const all = await fetchAllEntries(); setSelected(new Set(all.map((e) => e.id))); }
                        catch { toast.error('Could not select all attachments'); }
                      }} data-testid="vehicle-asset-logbook-select-all-toggle">
                        {selected.size >= totalCount ? 'Deselect All' : 'Select All'}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelected(new Set())} data-testid="vehicle-asset-logbook-clear-selection">Clear</Button>
                    </>)}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => load(filters)} data-testid="vehicle-asset-logbook-refresh-button">
                    <RefreshCw className={`w-4 h-4 mr-2 ${state.loading ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
                </div>
                );
              })()}

              {/* ── Attachment cards (outer card + nested asset row, mirrors trip cards) ── */}
              <div className="space-y-3">
              {pageEntries.map((e, i) => {
                const idx = pageStart + i;
                const tm = TYPE_META[String(e.role || '').toLowerCase()] || TYPE_META[String(e.person_type || '').toLowerCase()] || { label: e.role || 'Asset', icon: Container, cls: '' };
                const Icon = tm.icon;
                const startUrl = tripLocMapsUrl(e.location);
                const endUrl = tripLocMapsUrl(e.end_location);
                return (
                  <div key={e.id || idx} className={`rounded-xl border p-3.5 space-y-3 bg-white dark:bg-slate-800/50 shadow-[0_2px_8px_rgba(15,23,42,0.08)] dark:shadow-none transition-shadow ${selected.has(e.id) ? 'border-[#2563eb] ring-2 ring-[#2563eb]/40' : 'border-slate-300 dark:border-slate-700 hover:shadow-md'}`} data-testid={`vehicle-asset-logbook-entry-${idx}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <Checkbox checked={selected.has(e.id)} onCheckedChange={() => toggleSelection(e.id)} className="mt-1" data-testid={`vehicle-asset-logbook-entry-select-${idx}`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Route className="w-4 h-4 text-[#2563eb] shrink-0" />
                            <p className="font-semibold text-sm" data-testid={`vehicle-asset-logbook-time-${idx}`}>
                              {formatDT(e.started_at)} <span className="text-muted-foreground font-normal">{'\u2192'}</span> {e.is_active ? 'Now' : formatDT(e.ended_at)}
                            </p>
                            {e.is_active && <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid={`vehicle-asset-logbook-active-${idx}`}>Attached now</Badge>}
                          </div>
                          {(startUrl || endUrl) && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-1 text-[11px]" data-testid={`vehicle-asset-logbook-locations-${idx}`}>
                              {startUrl && (
                                <a href={startUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#2563eb] hover:underline" title="Open attach location in Google Maps" data-testid={`vehicle-asset-logbook-attach-loc-${idx}`}>
                                  <MapPin className="w-3 h-3 shrink-0" /> <span className="text-muted-foreground">Attach:</span> {fmtCoords(e.location)}
                                </a>
                              )}
                              {endUrl && (
                                <a href={endUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#2563eb] hover:underline" title="Open detach location in Google Maps" data-testid={`vehicle-asset-logbook-detach-loc-${idx}`}>
                                  <MapPin className="w-3 h-3 shrink-0" /> <span className="text-muted-foreground">Detach:</span> {fmtCoords(e.end_location)}
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground shrink-0 space-y-1">
                        {e.duration_seconds != null && (
                          <p className="flex items-center justify-end gap-1" data-testid={`vehicle-asset-logbook-duration-${idx}`}><Clock className="w-3 h-3" /> {formatDuration(e.duration_seconds)}</p>
                        )}
                        <div className="flex items-center justify-end gap-1 pt-0.5">
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => { logExport('csv', 1); exportAssetEntryCsv(e, vehicleName, idx); }} data-testid={`vehicle-asset-logbook-entry-csv-${idx}`}><Upload className="w-3 h-3 mr-1" /> CSV</Button>
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => { logExport('pdf', 1); exportAssetEntryPdf(e, vehicleName, idx).catch(() => toast.error('Could not generate PDF')); }} data-testid={`vehicle-asset-logbook-entry-pdf-${idx}`}><Upload className="w-3 h-3 mr-1" /> PDF</Button>
                        </div>
                      </div>
                    </div>
                    {/* Nested asset row — mirrors the Driver Log Book occupant sub-card */}
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-2.5 py-2" data-testid={`vehicle-asset-logbook-asset-${idx}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${tm.cls}`} data-testid={`vehicle-asset-logbook-role-${idx}`}>
                            <Icon className="w-3 h-3" /> {tm.label}
                          </span>
                          <span className="font-medium truncate" data-testid={`vehicle-asset-logbook-name-${idx}`}>{e.entity_display_name || 'Unknown asset'}</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Attached {formatDT(e.started_at)}
                          {e.is_active ? ' · still attached' : <> {'\u2192'} Detached {formatDT(e.ended_at)}</>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                        <Badge variant="outline" className="flex items-center gap-1" data-testid={`vehicle-asset-logbook-source-${idx}`}>
                          {e.is_ble ? <Bluetooth className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
                          {e.source_label}
                        </Badge>
                        <span className="font-mono break-all" data-testid={`vehicle-asset-logbook-cred-${idx}`}>{e.credential_identifier || '-'}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
              {!total && !state.data?.hints?.length && (
                <p className="text-sm text-muted-foreground py-6 text-center" data-testid="vehicle-asset-logbook-empty">No trailer or equipment attachments found.</p>
              )}

              {/* ── Pagination (shared component) ── */}
              <PaginationFooter
                page={page}
                setPage={setPage}
                totalItems={total}
                itemsPerPage={entriesPerPage}
                setItemsPerPage={setEntriesPerPage}
                testIdPrefix="vehicle-asset-logbook"
                scrollTargetRef={entriesListRef}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
      <EmailReportModal open={emailModal.open} onOpenChange={(open) => setEmailModal((p) => ({ ...p, open }))} api={api} ctx={emailModal.ctx} />
    </>
  );
};
